import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, X, Check, Star, TrendingUp, Info, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { subscribeToPackages } from '../services/firestore';
import SEO from '../components/SEO';

const ComparePackages = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('table'); // 'table' or 'cards'

  // Get package IDs from URL
  const packageIds = searchParams.get('packages')?.split(',').filter(Boolean) || [];

  // Max 3 packages for comparison
  const MAX_COMPARE = 3;

  // Get category name helper
  const getCategoryName = (categoryId, categories) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || '';
  };

  // Fetch all packages data
  useEffect(() => {
    const unsubscribe = subscribeToPackages((data) => {
      const activePackages = data.filter(pkg => pkg.isActive !== false);
      setAllPackages(activePackages);

      // Find selected packages
      if (packageIds.length > 0) {
        const selected = activePackages.filter(pkg =>
          packageIds.includes(pkg.id)
        ).map(pkg => ({
          ...pkg,
          features: pkg.includes || [],
        }));

        setPackages(selected);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [packageIds]);

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Get all unique features across all packages
  const allFeatures = useMemo(() => {
    const featureSet = new Set();
    packages.forEach(pkg => {
      (pkg.features || []).forEach(feature => {
        featureSet.add(feature);
      });
    });
    return Array.from(featureSet);
  }, [packages]);

  // Get all unique attributes
  const allAttributes = useMemo(() => {
    const attrs = new Set();
    packages.forEach(pkg => {
      if (pkg.categoryId) attrs.add('Kategori');
      if (pkg.subcategoryName) attrs.add('Subkategori');
      if (pkg.price) attrs.add('Harga');
      if (pkg.duration) attrs.add('Durasi');
    });
    return Array.from(attrs);
  }, [packages]);

  // Get attribute value
  const getAttributeValue = (pkg, attr, categories) => {
    switch (attr) {
      case 'Kategori':
        return getCategoryName(pkg.categoryId, categories);
      case 'Subkategori':
        return pkg.subcategoryName || '-';
      case 'Harga':
        return formatPrice(pkg.price);
      case 'Durasi':
        return pkg.duration || '-';
      default:
        return '-';
    }
  };

  // Check if values are different across packages
  const hasDifference = (attr) => {
    if (packages.length < 2) return false;
    const values = packages.map(pkg => getAttributeValue(pkg, attr, allPackages));
    return values.some(v => v !== values[0]);
  };

  const hasFeatureDifference = (feature) => {
    if (packages.length < 2) return false;
    const values = packages.map(pkg => (pkg.features || []).some(f => f === feature));
    return values.some(v => v !== values[0]);
  };

  // Check if package has feature
  const hasFeature = (pkg, feature) => {
    return (pkg.features || []).some(f => f === feature);
  };

  // Get recommendation based on needs
  const getRecommendation = (needs) => {
    if (!needs || packages.length === 0) return null;

    const scores = packages.map(pkg => {
      let score = 0;
      let reasons = [];

      if (needs.priority === 'budget') {
        const minPrice = Math.min(...packages.map(p => p.price));
        if (pkg.price === minPrice) {
          score += 30;
          reasons.push('Harga paling terjangkau');
        }
      }

      const featureCount = pkg.features?.length || 0;
      if (needs.priority === 'features') {
        const maxFeatures = Math.max(...packages.map(p => p.features?.length || 0));
        if (featureCount === maxFeatures) {
          score += 30;
          reasons.push('Fitur terlengkap');
        }
      }

      if (pkg.popular) {
        score += 20;
        reasons.push('Paling populer');
      }

      const valueScore = featureCount / (pkg.price / 1000000);
      const avgValue = packages.reduce((sum, p) =>
        sum + (p.features?.length || 0) / (p.price / 1000000), 0) / packages.length;
      if (valueScore >= avgValue) {
        score += 20;
        reasons.push('Nilai terbaik');
      }

      return { pkg, score, reasons };
    });

    const winner = scores.reduce((best, current) =>
      current.score > best.score ? current : best
    );

    return winner;
  };

  // Remove package from comparison
  const removePackage = (packageId) => {
    const newIds = packageIds.filter(id => id !== packageId);
    if (newIds.length < 2) {
      navigate('/#services');
    } else {
      navigate(`/compare?packages=${newIds.join(',')}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4AF7A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#F7F5F2]/70">Memuat paket...</p>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-[#0A0A0A] border border-[#D4AF7A]/20 rounded-2xl p-8 max-w-md w-full text-center">
          <Info className="w-16 h-16 text-[#D4AF7A]/50 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#F7F5F2] mb-2">Tidak ada paket yang dipilih</h2>
          <p className="text-[#F7F5F2]/70 mb-6">
            Silakan pilih minimal 2 paket dari halaman layanan untuk membandingkan
          </p>
          <button
            onClick={() => navigate('/#services')}
            className="px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg transition-all"
          >
            Kembali ke Layanan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <SEO
        title="Bandingkan Paket Henna | Putri Henna Artist"
        description="Bandingkan paket henna wedding, engagement, dan lainnya. Temukan paket henna yang paling sesuai dengan kebutuhan acara spesial Anda."
        keywords="bandingkan paket henna, compare henna packages, harga henna wedding, paket henna termurah"
        url="https://tehputrihenna.com/compare"
      />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-[#D4AF7A]/20">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <button
              onClick={() => navigate('/#services')}
              className="flex items-center gap-2 text-[#F7F5F2]/70 hover:text-[#D4AF7A] transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Kembali</span>
            </button>

            <h1 className="text-xl sm:text-2xl font-bold text-[#F7F5F2]">
              Bandingkan Paket
            </h1>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveView(activeView === 'table' ? 'cards' : 'table')}
                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] text-sm font-medium transition-all"
              >
                {activeView === 'table' ? 'Kartu' : 'Tabel'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Selected Packages Summary */}
          <div className="mb-8 p-4 sm:p-6 bg-[#0A0A0A] border border-[#D4AF7A]/20 rounded-xl">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-[#F7F5F2] mb-1">
                  Membandingkan {packages.length} Paket
                </h2>
                <p className="text-[#F7F5F2]/60 text-sm">
                  {packages.map(p => p.name).join(' vs ')}
                </p>
              </div>

              {/* Recommendation Buttons */}
              {packages.length >= 2 && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      const rec = getRecommendation({ priority: 'budget' });
                      setRecommendation(rec);
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-green-500/20 border border-green-500/30 rounded-full text-green-400 text-xs sm:text-sm font-medium hover:bg-green-500/30 transition-all"
                  >
                    💰 Terbaik Budget
                  </button>
                  <button
                    onClick={() => {
                      const rec = getRecommendation({ priority: 'features' });
                      setRecommendation(rec);
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-400 text-xs sm:text-sm font-medium hover:bg-blue-500/30 transition-all"
                  >
                    ⭐ Fitur Lengkap
                  </button>
                  <button
                    onClick={() => {
                      const rec = getRecommendation({ priority: 'value' });
                      setRecommendation(rec);
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-400 text-xs sm:text-sm font-medium hover:bg-purple-500/30 transition-all"
                  >
                    💎 Nilai Terbaik
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Recommendation Banner */}
          {recommendation && (
            <div className="mb-8 p-4 sm:p-6 bg-gradient-to-r from-[#C9A882]/20 to-[#D4AF7A]/20 border border-[#D4AF7A]/40 rounded-xl">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Star className="w-6 h-6 text-[#D4AF7A] fill-[#D4AF7A] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#D4AF7A] font-bold text-lg">Rekomendasi Kami:</span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#F7F5F2] mb-2">
                      {recommendation.pkg.name}
                    </h3>
                    <p className="text-[#F7F5F2]/80 text-sm sm:text-base">
                      {recommendation.reasons.join(' • ')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setRecommendation(null)}
                  className="p-2 text-[#F7F5F2]/70 hover:text-[#F7F5F2] hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          )}

          {/* View Mode: Cards */}
          {activeView === 'cards' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {packages.map(pkg => (
                <div
                  key={pkg.id}
                  className={`bg-[#0A0A0A] border rounded-2xl overflow-hidden ${
                    recommendation?.pkg.id === pkg.id
                      ? 'border-[#D4AF7A] ring-2 ring-[#D4AF7A]/50'
                      : 'border-[#D4AF7A]/20'
                  }`}
                >
                  {/* Card Header */}
                  <div className={`p-4 sm:p-6 ${
                    recommendation?.pkg.id === pkg.id ? 'bg-[#D4AF7A]/10' : ''
                  }`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg sm:text-xl font-bold text-[#F7F5F2]">
                            {pkg.name}
                          </h3>
                          {pkg.popular && (
                            <Star size={16} className="text-[#D4AF7A] fill-[#D4AF7A] flex-shrink-0" />
                          )}
                          {recommendation?.pkg.id === pkg.id && (
                            <span className="px-2 py-0.5 bg-[#D4AF7A] text-black text-xs rounded-full font-medium flex-shrink-0">
                              Rekomendasi
                            </span>
                          )}
                        </div>
                        {pkg.type && (
                          <p className="text-[#F7F5F2]/60 text-sm">{pkg.type}</p>
                        )}
                      </div>
                      <button
                        onClick={() => removePackage(pkg.id)}
                        className="p-1.5 text-[#F7F5F2]/50 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors flex-shrink-0"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div className="text-2xl sm:text-3xl font-bold text-[#D4AF7A] mb-2">
                      {formatPrice(pkg.price)}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 sm:p-6 space-y-4">
                    {/* Attributes */}
                    {allAttributes.length > 0 && (
                      <div className="space-y-2">
                        {allAttributes.map(attr => (
                          <div key={attr} className="flex justify-between items-center py-2 border-b border-white/5">
                            <span className="text-[#F7F5F2]/60 text-sm">{attr}</span>
                            <span className="text-[#F7F5F2] font-medium text-sm text-right">
                              {getAttributeValue(pkg, attr, allPackages)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Features Count */}
                    <div className="flex items-center justify-between py-2">
                      <span className="text-[#F7F5F2]/60 text-sm">Total Fitur</span>
                      <span className="text-[#D4AF7A] font-bold text-lg">
                        {pkg.features?.length || 0}
                      </span>
                    </div>

                    {/* Book Button */}
                    <a
                      href={`https://wa.me/6289526775445?text=Halo teh, aku mau booking ${pkg.name} untuk tanggal ....`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
                    >
                      Pesan Paket
                      <ArrowRight size={18} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* View Mode: Table */}
          {activeView === 'table' && (
            <div className="bg-[#0A0A0A] border border-[#D4AF7A]/20 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                  {/* Package Header */}
                  <div className={`grid grid-cols-${Math.min(packages.length + 1, 4)} gap-4 p-6 border-b border-[#D4AF7A]/20`}>
                    <div className="text-[#F7F5F2]/60 font-medium">
                      Fitur
                    </div>
                    {packages.map(pkg => (
                      <div
                        key={pkg.id}
                        className={`text-center p-4 rounded-lg ${
                          recommendation?.pkg.id === pkg.id
                            ? 'bg-[#D4AF7A]/20 border-2 border-[#D4AF7A]'
                            : 'bg-white/5 border border-[#D4AF7A]/20'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <h3 className="font-bold text-[#F7F5F2] text-sm">{pkg.name}</h3>
                          {pkg.popular && (
                            <Star size={14} className="text-[#D4AF7A] fill-[#D4AF7A]" />
                          )}
                        </div>
                        {pkg.type && (
                          <p className="text-[#F7F5F2]/60 text-xs mb-2">{pkg.type}</p>
                        )}
                        <div className="text-xl font-bold text-[#D4AF7A]">
                          {formatPrice(pkg.price)}
                        </div>
                        <button
                          onClick={() => removePackage(pkg.id)}
                          className="mt-2 text-xs text-[#F7F5F2]/50 hover:text-red-400 transition-colors"
                        >
                          <X size={14} className="inline mr-1" />
                          Hapus
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Attributes */}
                  {allAttributes.length > 0 && (
                    <div className="border-b border-[#D4AF7A]/20">
                      <h4 className="px-6 py-3 text-[#F7F5F2] font-semibold bg-white/5 flex items-center gap-2 text-sm">
                        <Info size={16} className="text-[#D4AF7A]" />
                        Informasi Paket
                      </h4>
                      {allAttributes.map(attr => {
                        const different = hasDifference(attr);
                        return (
                          <div
                            key={attr}
                            className={`grid grid-cols-${Math.min(packages.length + 1, 4)} gap-4 px-6 py-3 border-b border-white/5 ${
                              different ? 'bg-[#D4AF7A]/5' : ''
                            }`}
                          >
                            <div className="text-[#F7F5F2]/80 text-sm font-medium flex items-center gap-2">
                              {attr}
                              {different && (
                                <span className="px-2 py-0.5 bg-[#D4AF7A]/20 text-[#D4AF7A] text-xs rounded-full">
                                  Beda
                                </span>
                              )}
                            </div>
                            {packages.map(pkg => (
                              <div
                                key={pkg.id}
                                className={`text-center text-sm ${
                                  different ? 'text-[#D4AF7A] font-semibold' : 'text-[#F7F5F2]/70'
                                }`}
                              >
                                {getAttributeValue(pkg, attr, allPackages)}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Features */}
                  <div>
                    <h4 className="px-6 py-3 text-[#F7F5F2] font-semibold bg-white/5 flex items-center gap-2 text-sm">
                      <Check size={16} className="text-[#D4AF7A]" />
                      Fitur & Layanan
                    </h4>
                    {allFeatures.map(feature => {
                      const different = hasFeatureDifference(feature);
                      return (
                        <div
                          key={feature}
                          className={`grid grid-cols-${Math.min(packages.length + 1, 4)} gap-4 px-6 py-3 border-b border-white/5 ${
                            different ? 'bg-[#D4AF7A]/5' : ''
                          }`}
                        >
                          <div className="text-[#F7F5F2]/80 text-sm font-medium">
                            <span className="flex items-center gap-2">
                              {feature}
                              {different && (
                                <span className="px-2 py-0.5 bg-[#D4AF7A]/20 text-[#D4AF7A] text-xs rounded-full">
                                  Beda
                                </span>
                              )}
                            </span>
                          </div>
                          {packages.map(pkg => {
                            const hasIt = hasFeature(pkg, feature);
                            return (
                              <div
                                key={pkg.id}
                                className={`text-center ${
                                  hasIt ? 'text-green-400' : 'text-[#F7F5F2]/30'
                                }`}
                              >
                                {hasIt ? <Check size={18} /> : <span className="text-sm">-</span>}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>

                  {/* Feature Stats */}
                  <div className={`grid grid-cols-${Math.min(packages.length + 1, 4)} gap-4 p-6 border-t border-[#D4AF7A]/20 bg-white/5`}>
                    <div className="text-[#F7F5F2]/60 text-sm">
                      Total Fitur
                    </div>
                    {packages.map(pkg => (
                      <div key={pkg.id} className="text-center">
                        <div className="text-2xl font-bold text-[#D4AF7A]">
                          {pkg.features?.length || 0}
                        </div>
                        <div className="text-[#F7F5F2]/60 text-xs">fitur</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            {packages.map(pkg => (
              <a
                key={pkg.id}
                href={`https://wa.me/6289526775445?text=Halo teh, aku mau booking ${pkg.name} untuk tanggal ....`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 transition-all"
              >
                Pesan {pkg.name}
                <ArrowRight size={18} />
              </a>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ComparePackages;
